import React, { FunctionComponent } from 'react';
import { Query } from 'react-apollo';
import { ApolloError, DocumentNode, FetchPolicy } from 'apollo-boost';
import { mergeDeepWithKey, concat } from 'ramda';

import Loading from 'components/Loading';
import ErrorBoundary from 'components/ErrorBoundary';
import { getGraphQLError, defaultErrorMessage } from 'helpers/getGraphQLError';

type Props = {
  query: DocumentNode;
  variables?: any;
  render: Function;
  enableProgressInfo?: boolean;
  fetchPolicy?: FetchPolicy;
  notifyOnNetworkStatusChange?: boolean;
  skip?: boolean;
  onCompleted?: ((data: any) => void) | undefined;
};

type ResponseParams = {
  data?: Record<string, unknown>;
  loading?: boolean;
  error?: ApolloError;
  fetchMore: Function;
};

const concatValues = (key: string, previousValue: any, currentValue: any) =>
  key === 'result' ? concat(previousValue, currentValue) : currentValue;

const loadMore = (fetchMore: Function) => (offset: number, limit: number) =>
  fetchMore({
    variables: {
      params: {
        pagingInput: { offset, limit },
      },
    },
    updateQuery: (previousResult: Response, { fetchMoreResult = {} }: any) =>
      mergeDeepWithKey(concatValues, previousResult, fetchMoreResult),
  });

const QueryWrapper: FunctionComponent<Props> = ({
  query,
  variables,
  render,
  enableProgressInfo,
  fetchPolicy,
  notifyOnNetworkStatusChange,
  skip,
  onCompleted,
}) => (
  <ErrorBoundary>
    <Query
      notifyOnNetworkStatusChange={notifyOnNetworkStatusChange}
      fetchPolicy={fetchPolicy}
      query={query}
      variables={variables}
      skip={skip}
      onCompleted={onCompleted}
    >
      {({ loading, error, data, fetchMore: fetchMoreFunc }: ResponseParams) => {
        if (loading && !enableProgressInfo) {
          return <Loading loading={loading} />;
        }

        if (error) {
          getGraphQLError(defaultErrorMessage, true, error);
        }

        if (enableProgressInfo) {
          return render(
            {
              data,
              loading,
            },
            loadMore(fetchMoreFunc),
          );
        }

        return render(data);
      }}
    </Query>
  </ErrorBoundary>
);

export default QueryWrapper;
